import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { tagSession, trackEvent } from '../analytics/posthog';
import { AppLoading } from '../marketing/app-loading';
import { entryOf } from './extension-entry';
import { adoptExtensionSession } from './extension-session';
import { loginPathFor, safeRedirect } from './redirect';

/**
 * `/auth/extension?extId=<id>&redirect=<path>` — where the Floto Chrome
 * extension opens canvas. Takes the extension's session if canvas has none,
 * then moves on to `redirect`; falls back to `/login` if there is nothing to
 * take. See `adoptExtensionSession`.
 *
 * Outside `/login` on purpose: the extension's `externally_connectable` lists
 * only this path for canvas, so no other page can ask it for a token.
 */
export function ExtensionHandoffPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // StrictMode runs effects twice; one handshake is enough.
  const started = useRef(false);

  const extId = searchParams.get('extId');
  const redirect = safeRedirect(searchParams.get('redirect'));

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Every event after this one says which extension path it came in on —
    // the funnels for the two paths diverge on the very next screen.
    const entry = entryOf(redirect);
    tagSession(entry);
    trackEvent('extension_handoff_started', entry);

    void adoptExtensionSession(queryClient, extId).then((signedIn) => {
      // ₹tectedRoute takes it from here — onboarding included.
      // Tagged, so the room this ends in knows the extension sent them and
      // can say what a room is (`SessionPage`'s intro).
      void navigate(signedIn ? redirect : loginPathFor(redirect), {
        replace: true,
        ...(signedIn ? { state: { from: 'extension' } } : {}),
      });
    });
  }, [extId, navigate, queryClient, redirect]);

  return <AppLoading label="Signing you in…" />;
}
